import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { trackEvent, QUIZ_URL } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/women", label: "Women" },
  { to: "/coaches", label: "Coaches" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  const goQuiz = (loc) => {
    trackEvent("cta_click", { location: loc });
    setOpen(false);
    window.location.href = QUIZ_URL;
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between" aria-label="Main navigation">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group" onClick={() => setOpen(false)}>
          <span className="w-3.5 h-3.5 bg-volt group-hover:rotate-45 transition-transform duration-300" />
          <span className="font-display font-800 font-extrabold tracking-tight text-lg md:text-xl">
            WIRED<span className="text-volt">2</span>WIN
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-volt" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            data-testid="nav-quiz-cta"
            onClick={() => goQuiz("navbar")}
            className="group flex items-center gap-1.5 bg-volt text-obsidian font-display font-bold text-sm px-5 py-2.5 hover:bg-white transition-colors duration-200"
          >
            Take the Quiz
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-obsidian/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-display text-2xl font-bold ${isActive ? "text-volt" : "text-white/80"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                data-testid="nav-mobile-quiz-cta"
                onClick={() => goQuiz("mobile-menu")}
                className="mt-2 bg-volt text-obsidian font-display font-bold px-5 py-3.5 text-left"
              >
                Take the Free Quiz
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
